import type { Tour } from '../tour-types';

/**
 * The first time someone sees the app, after onboarding or after accepting an invite.
 * The sidebar and the header only: they are on every screen, so it cannot break on a slow
 * page, and each feature explains itself in its own tour later. Sidebar anchors are set in
 * ConsoleSidebar from the menu keys; the header's on the scope pickers, the usage meter, the AI
 * credit meter and the notification bell. On a phone the header steps that are in the closed drawer drop out.
 */
export const platformTour: Tour = {
  key: 'platform',
  version: 3,
  steps: [
    {
      anchor: 'nav-dashboard',
      title: 'Your dashboard',
      body: 'Start here. It shows what needs you, how your running experiments are doing, and your traffic.',
    },
    {
      // Only agencies have this row, so for everyone else the step drops out.
      anchor: 'nav-clients',
      title: 'Your clients',
      body: 'Each client has its own websites, experiments and plan. Pick one in the header to work inside it.',
    },
    {
      anchor: 'nav-monitors',
      title: 'Monitors',
      body: 'Health lists what needs a person. Performance tracks running experiments, and Program sums up what finished ones taught you.',
    },
    {
      anchor: 'nav-experiments',
      title: 'Experiments',
      body: 'Plan, build and read your A/B tests in the Library, and line ideas up in stages under Pipelines. Combination Report checks whether two experiments affected each other, and Implementation lists winners to ship.',
    },
    {
      anchor: 'nav-experiments.learnings',
      since: 2,
      title: 'Learnings',
      body: 'What your finished tests add up to: patterns, surprises and the open questions worth testing next, written by AI from your own results.',
    },
    {
      // Its own section since P-PER-59, so everyone who saw an earlier version hears of it.
      anchor: 'nav-flags',
      since: 3,
      title: 'Feature flags',
      body: 'Turn a change on for a share of visitors and widen it step by step, and turn it off at once if it goes wrong. For quick fixes made in the editor, or flags in your own code.',
    },
    {
      anchor: 'nav-surveys',
      title: 'Surveys',
      body: 'Ask visitors a question on your site, such as an NPS, CSAT or CES survey, and read the answers as they come in.',
    },
    {
      anchor: 'nav-websites',
      title: 'Websites',
      body: 'The sites you test on, with the snippet each needs, how it is tracked and the goals experiments are measured on. Google Analytics connects a GA4 account if you measure with it.',
    },
    {
      // Raised to since 3 for Agent access (PER-328), so people who saw version 2 get it again,
      // marked New. The step stays on the Settings row: the section is collapsed (its rows
      // unmounted) everywhere but /settings, so an anchor on the Agent Access row would never be
      // found and would hold back every page tour for them. Anchors are unique per tour, so this
      // step carries the news rather than a second one on the same row.
      anchor: 'nav-settings',
      since: 3,
      title: 'Settings',
      body: 'Your organization, your sign-in and emails, your team and billing, and now Agent access, where you give an AI agent such as Claude a token with only the scopes and websites it needs.',
    },
    {
      anchor: 'header-scope',
      placement: 'bottom',
      title: 'Account and website',
      body: 'Every screen shows the account and website picked here. Switch them to work on another account or site.',
    },
    {
      anchor: 'header-usage',
      placement: 'bottom',
      title: 'Usage',
      body: 'Sessions used against your plan this period. It turns yellow near the limit and red over it; click it for Billing.',
    },
    {
      anchor: 'header-ai-credits',
      placement: 'bottom',
      since: 2,
      title: 'AI credits',
      body: 'The credits the AI assistants can still spend this month. It turns amber when low and red when empty; click it for details.',
    },
    {
      anchor: 'header-notifications',
      placement: 'bottom',
      title: 'Notifications',
      body: 'Experiment progress and results, health warnings, and team and billing changes land here. Choose which also reach your inbox in Settings, Account.',
    },
    {
      anchor: 'tour-help',
      placement: 'top',
      title: 'Replay any tour',
      body: 'Take this tour again, or get a tour of the page you are on, from here.',
    },
  ],
};
